import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30000;

const fmtMoney = (n) => {
  const v = Number(n) || 0;
  if (v >= 10000) return `$${Math.round(v / 1000)}k`;
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${Math.round(v)}`;
};

/**
 * Tiny circular progress ring rendered to the LEFT of the pill text.
 * `pct` 0–100. Animates on mount and on prop change.
 */
const ProgressRing = ({ pct }) => {
  const size = 30;
  const stroke = 3;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const offset = c * (1 - clamped / 100);
  return (
    <span className="relative w-[30px] h-[30px] flex-shrink-0" aria-hidden="true">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.28)" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke="#FFFFFF" strokeWidth={stroke} fill="none"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 700ms ease-out' }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center">
        <Heart className="w-3 h-3 text-white" />
      </span>
    </span>
  );
};

/**
 * Floating bottom-right "Donate Now" CTA used across the public site, Shop,
 * and Pop-Up Shop pages.
 *
 * Behavior:
 *  - Polls /api/public/fundraising-goal every 30s.
 *  - When a goal is active and pct < 100, shows a thin progress ring + a
 *    micro "$X / $Y raised" line beneath the "Donate Now" label and pulses
 *    a subtle red halo every 2s.
 *  - Falls back to the original compact pill when there's no campaign data
 *    (or when the API call fails) — never blocks rendering.
 *
 * Props:
 *  - lifted: boolean — when true, sits higher to avoid overlapping a sticky
 *    checkout bar (e.g. pop-up shop cart bar). Default false.
 */
const FloatingDonateTab = ({ lifted = false }) => {
  const [goal, setGoal] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/public/fundraising-goal`);
        if (!cancelled) setGoal(r.data);
      } catch {
        if (!cancelled) setGoal(null);
      }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const hasActiveCampaign =
    goal &&
    Number(goal.goal_amount) > 0 &&
    Number(goal.current_amount || 0) < Number(goal.goal_amount);
  const pct = hasActiveCampaign
    ? Math.min(100, (Number(goal.current_amount || 0) / Number(goal.goal_amount)) * 100)
    : 0;

  const bottomClass = lifted
    ? 'bottom-[calc(260px+env(safe-area-inset-bottom))] sm:bottom-[calc(220px+env(safe-area-inset-bottom))]'
    : 'bottom-[calc(0.75rem+env(safe-area-inset-bottom))]';

  // Halo wrapper class only when a campaign is active — adds a gentle pulse
  // halo that doesn't change the button size.
  const haloClass = hasActiveCampaign ? 'nhf-donate-halo' : '';

  return (
    <div
      className={`fixed ${bottomClass} right-4 sm:right-6 z-[99999] ${haloClass}`}
      data-testid="floating-donate-tab"
      data-floating-tab="donate"
    >
      <Link
        to="/donate"
        className="group flex items-center gap-2.5 bg-[#BA1826] text-white pl-3 pr-5 py-2.5 rounded-full shadow-[0_4px_20px_rgba(186,24,38,0.4)] hover:bg-[#A41623] hover:shadow-[0_6px_28px_rgba(186,24,38,0.5)] transition-all duration-300 hover:scale-105"
        data-testid="floating-donate-btn"
      >
        {hasActiveCampaign ? (
          <ProgressRing pct={pct} />
        ) : (
          <Heart className="w-5 h-5 text-white flex-shrink-0 ml-1" />
        )}

        <span className="flex flex-col items-start leading-tight">
          <span className="text-sm font-bold whitespace-nowrap">Donate Now</span>
          {hasActiveCampaign && (
            <span
              className="text-[10px] font-medium text-white/85 whitespace-nowrap tracking-wide"
              data-testid="floating-donate-progress"
            >
              {fmtMoney(goal.current_amount)} / {fmtMoney(goal.goal_amount)} · {Math.round(pct)}%
            </span>
          )}
        </span>

        <ArrowRight className="w-4 h-4 text-white/80 flex-shrink-0" />
      </Link>
    </div>
  );
};

export default FloatingDonateTab;
